import React, { useState, useEffect } from 'react';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';

const SECTIONS = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'who-we-are', title: '2. Who We Are' },
  { id: 'information-collected', title: '3. Information We Collect' },
  { id: 'legal-basis', title: '4. Legal Basis for Processing' },
  { id: 'how-we-use', title: '5. How We Use Your Information' },
  { id: 'non-custodial', title: '6. Non-Custodial Nature' },
  { id: 'data-sharing', title: '7. Data Sharing & Third Parties' },
  { id: 'international-transfers', title: '8. International Transfers' },
  { id: 'data-security', title: '9. Data Security' },
  { id: 'data-retention', title: '10. Data Retention' },
  { id: 'cookies', title: '11. Cookies & Tracking' },
  { id: 'your-rights', title: '12. Your Rights' },
  { id: 'childrens-privacy', title: '13. Children\'s Privacy' },
  { id: 'third-party-links', title: '14. Third-Party Links' },
  { id: 'changes', title: '15. Changes to This Policy' },
  { id: 'contact', title: '16. Contact & DPO' },
];

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-base font-bold text-foreground mb-4 pb-2 border-b border-border">{title}</h2>
    {children}
  </section>
);

const Para = ({ children }) => (
  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>
);

const List = ({ items }) => (
  <ul className="space-y-1.5 text-sm text-muted-foreground mb-3 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const SubSection = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
    {children}
  </div>
);

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="flex-1">
      <div className="max-w-7xl mx-auto px-6 py-16" data-testid="privacy-page">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-3 block">Legal</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: <strong className="text-foreground">February 2026</strong> &nbsp;·&nbsp; Effective: <strong className="text-foreground">February 2026</strong></p>
          <p className="text-sm text-muted-foreground mt-2">This policy describes how CoinPayUp collects, uses, and protects your personal information when you use our platform and services.</p>
        </div>

        <div className="flex gap-10 items-start">
          {/* Sticky Table of Contents */}
          <aside className="hidden xl:block w-56 shrink-0 sticky top-24">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map(({ id, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
                  className={`block text-xs py-1.5 px-2.5 rounded-md transition-all ${
                    activeSection === id
                      ? 'text-primary bg-primary/8 font-semibold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  {title}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-3xl bg-card border border-border rounded-2xl p-8 lg:p-10">

            <Section id="introduction" title="1. Introduction">
              <Para>
                Welcome to CoinPayUp. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong className="text-foreground">coinpayup.com</strong> and use our cryptocurrency payment gateway services.
              </Para>
              <Para>
                Please read this policy carefully. If you disagree with its terms, please discontinue use of our services. By accessing or using CoinPayUp, you consent to the collection and use of information as described herein.
              </Para>
            </Section>

            <Section id="who-we-are" title="2. Who We Are">
              <Para>
                CoinPayUp operates as a non-custodial cryptocurrency payment verification platform. For the purposes of applicable data protection law, CoinPayUp acts in a dual capacity:
              </Para>
              <List items={[
                'Data Controller: for account registration data, profile information, and operational data you provide directly to CoinPayUp',
                'Data Processor: for payment transaction data processed on behalf of merchants in connection with their customers\' payments',
              ]} />
              <Para>
                When acting as a data processor, CoinPayUp processes data strictly on the instruction of the merchant (the data controller), in accordance with our Data Processing terms. Merchants are themselves responsible for ensuring they have a lawful basis to collect and share their customers\' data with CoinPayUp.
              </Para>
              <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground space-y-1.5 mt-3">
                <p className="font-semibold text-foreground">Legal Entity Details</p>
                <p><strong className="text-foreground">Legal Name:</strong> <span className="italic">[Registered Company Name]</span></p>
                <p><strong className="text-foreground">Country of Registration:</strong> <span className="italic">[Country]</span></p>
                <p><strong className="text-foreground">Registered Address:</strong> <span className="italic">[Full Registered Business Address]</span></p>
                <p><strong className="text-foreground">Website:</strong> <a href="https://coinpayup.com" className="text-primary hover:underline">coinpayup.com</a></p>
                <p><strong className="text-foreground">Privacy & DPO Contact:</strong> <a href="mailto:privacy@coinpayup.com" className="text-primary hover:underline">privacy@coinpayup.com</a></p>
                <p className="text-xs pt-2 border-t border-border text-muted-foreground/70">For general support, please use the support ticket system available from your merchant dashboard.</p>
              </div>
            </Section>

            <Section id="information-collected" title="3. Information We Collect">
              <Para>We collect different types of information depending on how you interact with our platform:</Para>

              <SubSection title="3.1 Information You Provide Directly">
                <List items={[
                  'Account registration: full name, email address, business name, and password (stored as a bcrypt hash — never in plaintext)',
                  'Profile information: preferred currency, business details, and contact preferences',
                  'Support communications: messages, attachments, and correspondence with our support team',
                  'Payment configuration: webhook URLs, API key names, and wallet addresses you register',
                  'Verification documents: if requested for compliance purposes',
                ]} />
              </SubSection>

              <SubSection title="3.2 Transaction & Blockchain Data">
                <List items={[
                  'Invoice records: payment amounts, currencies, due dates, and invoice reference numbers',
                  'Blockchain transaction data: transaction hashes, sender wallet addresses, on-chain amounts, and confirmation timestamps',
                  'Payment verification logs: matched/unmatched payment records and tolerance calculations',
                  'Fee ledger entries: platform fees applied to processed transactions',
                ]} />
              </SubSection>

              <SubSection title="3.3 Automatically Collected Data">
                <List items={[
                  'Log data: IP addresses, browser type and version, operating system, referring URLs, and page views',
                  'Device information: device type, screen resolution, and hardware model',
                  'Usage analytics: features used, actions taken, session duration, and navigation patterns',
                  'Error and performance logs: crash reports, API errors, and response times',
                ]} />
              </SubSection>

              <SubSection title="3.4 Information from Third Parties">
                <List items={[
                  'Blockchain networks: on-chain transaction data from BSC, Ethereum, Polygon, and Tron networks',
                  'CAPTCHA providers (hCaptcha): verification tokens to prevent fraud',
                  'Email delivery services: delivery receipts and bounce notifications',
                ]} />
              </SubSection>
            </Section>

            <Section id="legal-basis" title="4. Legal Basis for Processing (GDPR)">
              <Para>If you are located in the European Economic Area (EEA) or United Kingdom, we process your personal data under the following legal bases:</Para>
              <List items={[
                'Contract performance (Art. 6(1)(b)): Processing necessary to provide the services you have contracted with us — account management, payment verification, invoicing, and API access',
                'Legitimate interests (Art. 6(1)(f)): Fraud detection, security monitoring, abuse prevention, service improvement, and analytics — where our interests do not override your fundamental rights and freedoms. We conduct balancing assessments to ensure proportionality',
                'Legal obligation (Art. 6(1)(c)): Compliance with applicable laws, financial regulations, anti-money laundering (AML) obligations, sanctions screening requirements, and court orders',
                'Consent (Art. 6(1)(a)): For optional communications such as marketing newsletters — you may withdraw consent at any time without affecting the lawfulness of prior processing',
                'Vital interests and public tasks: Where necessary to protect life or for tasks in the public interest (e.g., cooperating with law enforcement on serious crime investigations)',
              ]} />
              <Para>
                Where we rely on <strong className="text-foreground">legitimate interests</strong>, we have assessed that our interests are proportionate and do not override your fundamental rights and freedoms as a data subject. You have the right to object to processing based on legitimate interests at any time.
              </Para>
            </Section>

            <Section id="how-we-use" title="5. How We Use Your Information">
              <Para>We use the information we collect for the following purposes:</Para>

              <SubSection title="5.1 Service Delivery">
                <List items={[
                  'Creating and managing your merchant account',
                  'Processing and verifying cryptocurrency payments on your behalf',
                  'Generating and sending invoices to your customers',
                  'Delivering webhook notifications and API responses',
                  'Operating our support ticket system and responding to your inquiries',
                ]} />
              </SubSection>

              <SubSection title="5.2 Platform Operations & Compliance">
                <List items={[
                  'Monitoring blockchain node health and payment gateway uptime',
                  'Detecting, preventing, and investigating fraud, security incidents, abuse, and suspicious activity — including real-time transaction monitoring for AML and sanctions compliance',
                  'Conducting audits, debugging, and maintaining service reliability',
                  'Enforcing our Terms and Conditions and other policies',
                  'Investigating disputes, policy violations, or suspected fraud between merchants and customers',
                  'Complying with anti-money laundering (AML) laws, sanctions screening obligations, and requests from regulatory authorities or law enforcement where legally required',
                ]} />
              </SubSection>

              <SubSection title="5.3 Communications">
                <List items={[
                  'Sending transactional emails: payment confirmations, invoice receipts, password resets',
                  'Delivering system alerts: node health warnings, failed payment notifications',
                  'Service announcements: planned maintenance, policy updates, new features',
                  'Marketing communications (only with your explicit consent — you can unsubscribe at any time)',
                ]} />
              </SubSection>

              <SubSection title="5.4 Analytics & Improvement">
                <List items={[
                  'Analysing usage patterns to improve product features and user experience',
                  'Performing aggregated statistical analysis on platform performance',
                  'A/B testing new features and interface improvements',
                ]} />
              </SubSection>
            </Section>

            <Section id="non-custodial" title="6. Non-Custodial Nature & Financial Data">
              <Para>
                CoinPayUp is a <strong className="text-foreground">non-custodial</strong> payment verification service. This means:
              </Para>
              <List items={[
                'We do not hold, store, control, or have access to any cryptocurrency funds at any point',
                'All payments are transmitted directly from payers to merchant-owned blockchain wallets',
                'We only verify that on-chain transactions match expected payment parameters',
                'Wallet private keys are never requested, stored, or accessible by CoinPayUp',
                'We cannot initiate, reverse, freeze, or otherwise control blockchain transactions',
              ]} />
              <Para>
                Because of this architecture, the financial data we process is limited to transaction metadata (hashes, amounts, timestamps) — not the funds themselves.
              </Para>
              <div className="bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground mt-2">
                <p><strong className="text-foreground">No sensitive financial data stored:</strong> CoinPayUp does not store bank account details, credit card numbers, debit card numbers, or wallet private keys at any time. We are not a financial institution and do not process fiat currency payments.</p>
              </div>
            </Section>

            <Section id="data-sharing" title="7. Data Sharing & Third Parties">
              <Para>We do not sell, trade, or rent your personal information. We may share data only in these limited circumstances:</Para>

              <SubSection title="7.1 Service Providers">
                <Para>We engage trusted third-party vendors who process data on our behalf under strict data processing agreements:</Para>
                <List items={[
                  'Cloud infrastructure & hosting providers (for platform operation)',
                  'Email delivery services (for transactional and notification emails)',
                  'CAPTCHA providers (for fraud prevention)',
                  'Analytics platforms (for aggregated usage insights)',
                ]} />
              </SubSection>

              <SubSection title="7.2 Legal & Regulatory">
                <List items={[
                  'When required by applicable law, court order, or binding governmental authority',
                  'In response to lawful requests by public authorities, including national security or law enforcement',
                  'To enforce our Terms, protect our rights, or address illegal activity',
                ]} />
              </SubSection>

              <SubSection title="7.3 Business Transfers">
                <Para>In the event of a merger, acquisition, or sale of all or substantially all assets, your information may be transferred. We will provide notice before your data is subject to a different privacy policy.</Para>
              </SubSection>
            </Section>

            <Section id="international-transfers" title="8. International Data Transfers">
              <Para>
                Your information may be transferred to and processed in countries other than the country in which you reside. These countries may have data protection laws that differ from your country.
              </Para>
              <Para>
                When we transfer personal data outside the EEA or UK, we implement the following safeguards to ensure your data receives adequate protection:
              </Para>
              <List items={[
                'Standard Contractual Clauses (SCCs): We use EU Commission-approved SCCs for transfers to recipients in countries without an adequacy decision',
                'Adequacy decisions: Where the European Commission has recognised a country as providing adequate data protection, we rely on this decision',
                'Transfer impact assessments: Where required, we conduct transfer impact assessments to evaluate risks and implement supplementary measures',
                'Data minimisation: We transfer only the minimum necessary personal data required for the specific purpose',
              ]} />
              <Para>
                You may request information about the specific safeguards in place for any international transfer of your data by submitting a support ticket from your merchant dashboard.
              </Para>
            </Section>

            <Section id="data-security" title="9. Data Security">
              <Para>We implement industry-standard technical and organisational measures to protect your information:</Para>
              <List items={[
                'All passwords are hashed using bcrypt with unique salt rounds — plaintext passwords are never stored',
                'All data in transit is encrypted using TLS 1.2+ (HTTPS)',
                'API keys are stored using one-way cryptographic hashing',
                'Access to production databases is restricted to authorised personnel only',
                'Database access credentials are managed through environment variables, never hardcoded',
                'Regular security reviews, dependency audits, and vulnerability assessments',
                'Role-based access control (RBAC) limiting staff access to only what is needed',
                'Audit logs maintained for all sensitive administrative operations',
                'Activity monitoring to detect fraud, abuse, anomalous access patterns, and potential security threats',
              ]} />
              <Para>
                Despite our best efforts, no security measure is 100% impenetrable. We encourage you to use strong, unique passwords, enable 2FA, and report any suspected security incidents via our <strong className="text-foreground">support ticket system</strong>.
              </Para>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 mt-3 text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Data Breach Notification</p>
                <p>In the event of a personal data breach that is likely to result in a risk to the rights and freedoms of individuals, we will notify the relevant supervisory authority within <strong className="text-foreground">72 hours</strong> of becoming aware of the breach, where required by applicable law (e.g., GDPR Art. 33). Where the breach is likely to result in a high risk to affected individuals, we will also notify those individuals directly without undue delay (GDPR Art. 34), unless a lawful exemption applies.</p>
              </div>
            </Section>

            <Section id="data-retention" title="10. Data Retention">
              <Para>
                Retention periods are determined based on legal, regulatory, fraud prevention, operational, and contractual requirements. We retain personal information only for as long as necessary for the specific purpose for which it was collected, subject to applicable law.
              </Para>
              <List items={[
                'Account data: Retained for the duration of your active account, plus 90 days after closure to allow for dispute resolution',
                'Transaction & invoice records: Retained for a minimum of 7 years for financial regulatory and AML compliance requirements',
                'Support tickets & communications: Retained for 3 years after resolution for operational and legal purposes',
                'Log data & analytics: Retained for 12 months before aggregation or deletion',
                'Password reset tokens: Automatically deleted upon use or after 1 hour (whichever comes first)',
                'Data used in fraud investigations or legal proceedings: Retained for the duration of the investigation plus any applicable statutory limitation period',
              ]} />
              <Para>
                You may request deletion of your account and personal data by submitting a <strong className="text-foreground">support ticket</strong> from your merchant dashboard. Deletion requests are processed within 30 days, subject to legal retention requirements. Data that we are legally required to retain cannot be deleted before the applicable retention period expires.
              </Para>
            </Section>

            <Section id="cookies" title="11. Cookies & Tracking Technologies">
              <Para>CoinPayUp uses the following types of cookies and local storage:</Para>

              <SubSection title="11.1 Strictly Necessary">
                <Para>These are required for the platform to function. They store session tokens, authentication state, and user preferences (e.g., theme settings). You cannot opt out of these without losing platform functionality.</Para>
              </SubSection>

              <SubSection title="11.2 Functional / Preference">
                <Para>These remember your choices, such as display language, currency preferences, and notification settings. They improve your experience but are not strictly required.</Para>
              </SubSection>

              <SubSection title="11.3 Analytics">
                <Para>If analytics tools are active, cookies may track aggregated usage patterns (pages visited, feature interactions) to help us improve the product. No personally identifiable information is shared with analytics providers beyond what is necessary.</Para>
              </SubSection>

              <SubSection title="11.4 Cookie Consent">
                <Para>Where required by applicable law (including the EU ePrivacy Directive and GDPR), we obtain your consent before placing non-essential cookies on your device. Strictly necessary cookies do not require consent as they are essential for the platform to function. You may withdraw consent for non-essential cookies at any time by adjusting your browser settings or any cookie preference tool available on the platform.</Para>
              </SubSection>

              <Para>You may configure your browser to refuse cookies or to alert you when cookies are being sent. Note that disabling cookies may affect the functionality of our platform. We do not use any third-party advertising cookies or tracking pixels for advertising purposes.</Para>
            </Section>

            <Section id="your-rights" title="12. Your Rights">
              <Para>Depending on your jurisdiction, you may have the following rights regarding your personal data. We will respond to all verified requests within 30 days.</Para>

              <SubSection title="12.1 Rights Under GDPR (EEA / UK Residents)">
                <List items={[
                  'Right of access (Art. 15): Request a copy of the personal data we hold about you',
                  'Right to rectification (Art. 16): Request correction of inaccurate or incomplete data',
                  'Right to erasure / "right to be forgotten" (Art. 17): Request deletion of your data, subject to legal retention obligations',
                  'Right to restriction of processing (Art. 18): Request that we limit how we use your data',
                  'Right to data portability (Art. 20): Receive your data in a structured, machine-readable format',
                  'Right to object (Art. 21): Object to processing based on legitimate interests or direct marketing',
                  'Rights related to automated decision-making (Art. 22): We do not make solely automated decisions with significant effects',
                ]} />
                <Para>You also have the right to lodge a complaint with your local data protection authority (e.g., ICO in the UK, your national DPA in the EU).</Para>
              </SubSection>

              <SubSection title="12.2 Rights Under CCPA (California Residents)">
                <List items={[
                  'Right to know what personal information is collected, used, shared, or sold',
                  'Right to delete personal information held by us',
                  'Right to opt-out of the sale of personal information (we do not sell personal information)',
                  'Right to non-discrimination for exercising CCPA rights',
                ]} />
              </SubSection>

              <Para>To exercise any of these rights, please submit a <strong className="text-foreground">support ticket</strong> from your merchant dashboard with sufficient information to verify your identity.</Para>
            </Section>

            <Section id="childrens-privacy" title="13. Children's Privacy">
              <Para>
                CoinPayUp is not directed to, nor intended for, individuals under the age of 18. We do not knowingly collect personal information from minors. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately via our <strong className="text-foreground">support ticket system</strong>. We will take prompt steps to delete such information.
              </Para>
            </Section>

            <Section id="third-party-links" title="14. Third-Party Links & Services">
              <Para>
                Our platform may contain links to third-party websites, documentation, or services (e.g., blockchain explorers, payment documentation). These third parties have their own privacy policies and we have no control over their data practices. We strongly encourage you to review the privacy policies of any third-party services you access through our platform. We are not responsible for the privacy practices or content of third-party services.
              </Para>
            </Section>

            <Section id="changes" title="15. Changes to This Policy">
              <Para>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make significant changes, we will:
              </Para>
              <List items={[
                'Update the "Last updated" date at the top of this page',
                'Notify registered merchants via an in-platform notice',
                'Display a prominent notice on our platform for a reasonable period',
              ]} />
              <Para>Your continued use of CoinPayUp after changes take effect constitutes your acceptance of the revised Privacy Policy.</Para>
            </Section>

            <Section id="contact" title="16. Contact Us & DPO">
              <Para>If you have any questions, concerns, or requests regarding this Privacy Policy, our data practices, or to exercise your data subject rights, please contact our Privacy Team:</Para>
              <div className="bg-muted/30 rounded-xl p-5 text-sm text-muted-foreground space-y-2 mt-3">
                <p className="font-semibold text-foreground text-base">CoinPayUp — Privacy & DPO Contact</p>
                <p><strong className="text-foreground">Privacy & Data Requests:</strong> <a href="mailto:privacy@coinpayup.com" className="text-primary hover:underline">privacy@coinpayup.com</a></p>
                <p className="text-xs text-muted-foreground/70">Use this address for: GDPR data subject requests, data breach reports, DPO inquiries, and all privacy-related communications.</p>
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground/70">For general platform support, please use the <strong className="text-foreground">support ticket system</strong> available from your merchant dashboard.</p>
                </div>
                <p className="text-xs pt-1">We aim to respond to all privacy-related inquiries within <strong className="text-foreground">5 business days</strong>. For GDPR data subject requests, we will respond within <strong className="text-foreground">30 days</strong> as required by law.</p>
              </div>
            </Section>

          </div>
        </div>
      </div>
      </main>
      <SiteFooter />
    </div>
  );
}
